import { InjectionToken } from '@angular/core';

import type { Callback, CoreContext, CoreSegmentEvent } from '@segment/analytics-core';
import { type Observable, of } from 'rxjs';

export enum AnalyticsViewContext {
  Candidates = 'candidates',
  CandidateProfile = 'candidate_profile',
  CandidateProfileSidebar = 'candidate_profile_sidebar',
  Offer = 'offer',
  TalentPool = 'talent_pool',
  TalentPools = 'talent_pools',
  Notes = 'notes',
  OpenQuestionsTemplates = 'open_questions_templates',
  ProfileFields = 'profile_fields',
  Settings = 'settings',
  SettingsProfileCalendars = 'settings_profile_calendars',
  SettingsRequisitions = 'settings_requisitions',
  SettingsCustomFields = 'settings_custom_fields',
  EmailTemplates = 'email_templates',
  PipelineTemplates = 'pipelines_templates',
  DisqualifyReasons = 'disqualify_reasons',
  AutomatedActions = 'automated_actions',
  AutomationWizardDialog = 'automation_wizard_dialog',
  EmailForm = 'email_form',
  ScheduleDialog = 'schedule_dialog',
  Mailbox = 'mailbox',
  Dashboard = 'dashboard',
  EventSchedulerDialog = 'event_scheduler_dialog',
  Reports = 'reports',
  Pipeline = 'pipeline',
  Academy = 'academy',
  Jobs = 'jobs',
  JobEditor = 'job_editor',
  JobTemplateEditor = 'job_template_editor',
  JobPosting = 'job_posting',
  JobDetails = 'job_details',
  Calendar = 'calendar',
  Events = 'events',
  OfferPipeline = 'offer_pipeline',
  OfferFilters = 'offer_filters',
  Billing = 'billing',
  HeaderMenu = 'header_menu',
  FAB = 'FAB',
  TrialCenter = 'trial_center',
  NewsFeed = 'news_feed',
  Requisition = 'requisition',
  Requisitions = 'requisitions',
  OffersSelectDialog = 'offers_select_dialog',
  JobsWidget = 'jobs_widget',
  TutorialDialog = 'tutorial_dialog',
  VideoDialog = 'video_dialog',
  CreateNewRequisitionDialog = 'create_new_requisition_dialog',
  MarkAsHiredDialog = 'mark_as_hired_dialog',
  SidebarMenu = 'sidebar_menu',
  Texting = 'texting',
  Views = 'views',
  CopyPublicLinkDialog = 'dialog',
  CopyPublicLinkDropdown = 'dropdown',
  Template = 'template',
  GettingStartedDrawer = 'getting_started_drawer',
  DeleteEvent = 'delete_event',
  Acquisitions = 'acquisitions',
  CandidateMessages = 'candidate_messages',
  DashboardOverview = 'dashboard_overview',
  DashboardOverdueCandidatesWidget = 'dashboard_overdue_candidates_widget',
  DashboardNewCandidatesWidget = 'dashboard_new_candidates_widget',
  DashboardScheduleInterviewWidget = 'dashboard_schedule_interview_widget',
  DashboardJobsWidget = 'dashboard_jobs_widget',
  DashboardWidgetsDrawer = 'dashboard_widgets_drawer',
  MergingDialog = 'merging_dialog',
  TaskDialog = 'task_dialog',
  OfferLetterTemplateEditor = 'offer_letter_template_editor',
}

export enum AnalyticsMode {
  Single = 'single',
  Bulk = 'bulk',
}

export type PageTrackParamsSanitizerFn = (
  name: string,
  category: string,
  params: AnalyticsProps,
) => AnalyticsProps;

export interface AnalyticsConfigOptions {
  key?: string;
  segmentOptions?: AnalyticsClientOptions;
  debug?: boolean;
  disable?: boolean;
  waitForIdentify?: boolean;
  trackRouteChange: boolean;
  /**
   * If provided analytics will wait for this observable to emit before initializing
   * runs in injection context
   */
  initializeFactory?: () => Observable<boolean>;
  pageTrackParamsSanitizer?: PageTrackParamsSanitizerFn;
}

const noopPageTrackParamsSanitizer: PageTrackParamsSanitizerFn = (
  name: string,
  category: string,
  params: AnalyticsProps,
) => params;

export class AnalyticsConfig {
  private readonly defaultSegmentOptions: AnalyticsClientOptions = {};

  constructor(public opts: AnalyticsConfigOptions) {}

  public get pageTrackParamsSanitizer(): PageTrackParamsSanitizerFn {
    return this.opts.pageTrackParamsSanitizer ?? noopPageTrackParamsSanitizer;
  }

  public get initializeFactory(): () => Observable<boolean> {
    return this.opts.initializeFactory ?? (() => of(true));
  }

  public get isAvailable(): boolean {
    const resp = !this.opts.disable && this.opts.key !== undefined;
    return resp;
  }

  public get isDebug(): boolean {
    return this.opts.debug === true;
  }

  public get key(): string {
    return this.opts.key!;
  }

  public get options(): AnalyticsClientOptions {
    return this.opts.segmentOptions || this.defaultSegmentOptions;
  }

  public get trackRoutes() {
    return this.opts.trackRouteChange || false;
  }

  public get waitForIdentify() {
    return this.opts.waitForIdentify || false;
  }
}

export type AnalyticsProps<T = any> = Record<
  keyof T,
  null | undefined | string | number | boolean | Array<string | number | boolean | null>
>;

export type ClientIdentifyCallbackFn = Callback<CoreContext<CoreSegmentEvent>> | (() => unknown);

export type AnalyticsIntegrations = 'Mixpanel' | 'Intercom';

type JSONValue = string | number | boolean | null | JSONValue[] | { [key: string]: JSONValue };

export type AnalyticsClientOptions = {
  integrations?: { All?: boolean } & {
    [key in AnalyticsIntegrations]?: boolean | Record<string, JSONValue>;
  };
};

export interface AnalyticsAPIMethods {
  alias: (id: number) => void;
  track: (eventName: string, eventProps?: AnalyticsProps, opts?: AnalyticsClientOptions) => void;
  identify: (
    userId?: number,
    traits?: Record<string, string | number | boolean>,
    options?: AnalyticsClientOptions,
    callback?: ClientIdentifyCallbackFn,
  ) => void;
  page: (
    category: string,
    name: string,
    properties?: AnalyticsProps,
    options?: AnalyticsClientOptions,
  ) => void;
  reset: () => void;
}

export type AnalyticsEventDescriptor = {
  method: keyof AnalyticsAPIMethods;
  data: any;
  options: AnalyticsClientOptions;
};

export const Inherit = Symbol('Inherit');

export interface AnalyticsRouteData {
  analytics?:
    | {
        category: string;
        name: string;
        filter?: (params: Record<any, any>) => boolean;
      }
    | typeof Inherit;
}

export interface AnalyticsEnvironment {
  analytics?: {
    isEnabled: boolean;
    key: string;
    requiredFeatureFlag: boolean;
  };
}

export abstract class Analytics {
  public abstract alias(id: number): void;

  public abstract track(
    eventName: string,
    eventProps?: Record<string, boolean | string | number | undefined | null | string[]>,
    opts?: any,
  ): void;

  public abstract identify(
    userId?: number | string,
    traits?: Record<string, string | number>,
    options?: any,
  ): void;

  public abstract page(category?: string, name?: string, properties?: any, options?: any): void;
}

export const ANALYTICS_DISABLED = new InjectionToken<boolean>('Analytics disabled');
export const ANALYTICS_VIEW_CONTEXT = new InjectionToken<AnalyticsViewContext[]>(
  'Analytics view context',
);
export const ANALYTICS_VIEW_CONTEXT_LEAF = new InjectionToken<AnalyticsViewContext[]>(
  'Analytics context (leaf)',
);
